import {EBirdMyDataSchema} from "ebird-mydata-reader";
import {REGION_ENRICHMENT_ENDPOINT} from "./constants";
import {getApiUrl} from "./client";
import type {LocationRegionData} from "../helpers/types";

export type RegionEnrichmentLocation = {
    locationId: string,
    location: string,
    latitude: number,
    longitude: number,
    stateProvince: string,
    county: string
}

export type RegionEnrichmentResponse = {
    version: number,
    summary?: {
        locationCount: number,
        countryCount: number,
        stateCount: number,
        matchedCountryCount: number,
        matchedStateCount: number,
        matchedCountyCount: number,
        unmatchedCountyCount: number
    },
    locations: Record<string, LocationRegionData>
}

const getCoordinate = (coordinate: string | number | undefined) => {
    const parsedCoordinate = Number(coordinate);
    return Number.isFinite(parsedCoordinate) ? parsedCoordinate : undefined;
}

export const getUniqueRegionEnrichmentLocations = (observations: EBirdMyDataSchema[]): RegionEnrichmentLocation[] => {
    const locations = new Map<string, RegionEnrichmentLocation>();

    for (const observation of observations) {
        const latitude = getCoordinate(observation.latitude);
        const longitude = getCoordinate(observation.longitude);
        if (latitude === undefined || longitude === undefined) continue;

        const locationId = observation.locationId || `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
        if (locations.has(locationId)) continue;

        locations.set(locationId, {
            locationId,
            location: observation.location || '',
            latitude,
            longitude,
            stateProvince: observation.stateProvince || '',
            county: observation.county || ''
        });
    }

    return Array.from(locations.values());
}

export const enrichRegionLocations = async (locations: RegionEnrichmentLocation[]) => {
    if (!locations.length) return {};

    const response = await fetch(getApiUrl(REGION_ENRICHMENT_ENDPOINT), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({locations})
    });

    if (!response.ok) {
        throw new Error(`Region enrichment failed with ${response.status}`);
    }

    const enrichment: RegionEnrichmentResponse = await response.json();
    return enrichment.locations ?? {};
}
